import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import Header from "../components/Header";
import Footer from "../components/Footer";
import FloatingContact from "../components/FloatingContact";
import ApplicationDialog from "../components/ApplicationDialog";
import { useLang } from "../i18n/LangContext";
import blog, { LOCALE_TO_URL_SEG, LOCALE_TO_HTML_LANG } from "../i18n/blog_i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_URL = "https://studygri.com";

const BlogArticlePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { lang } = useLang();
  const t = blog[lang] || blog.uk;
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [formOpen, setFormOpen] = useState(false);

  const seg = LOCALE_TO_URL_SEG[lang] || 'ua';

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    axios.get(`${API}/articles/public/${lang}/${slug}`)
      .then(r => {
        if (cancelled) return;
        const a = r.data;
        // If the requested slug is old — client-side 301 to the new canonical URL.
        // Use basename-relative path so React Router prepends the current /{seg}.
        if (a.redirect_from_slug && a.slug !== slug) {
          navigate(`/blog/${a.slug}`, { replace: true });
          return;
        }
        setArticle(a);
      })
      .catch(() => { if (!cancelled) setNotFound(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug, lang, seg, navigate]);

  useEffect(() => {
    document.documentElement.lang = LOCALE_TO_HTML_LANG[lang] || lang;
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
    return () => { document.documentElement.dir = 'ltr'; };
  }, [lang]);

  if (notFound) {
    return (
      <div className="min-h-screen" style={{ background: '#FAF8F5' }}>
        <Helmet><title>404 — STUDYGRI</title><meta name="robots" content="noindex" /></Helmet>
        <Header onOpenForm={() => setFormOpen(true)} />
        <div className="pt-40 pb-40 text-center px-6">
          <div className="font-display text-3xl mb-4" style={{ color: '#1B2A4A' }}>404</div>
          <Link to="/blog" className="text-sm font-semibold hover:text-[#C9A96E]" style={{ color: '#1B2A4A' }}>{t.back}</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const fmtDate = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleDateString(lang === 'uk' ? 'uk-UA' : lang, { day: '2-digit', month: 'long', year: 'numeric' }); }
    catch { return iso.slice(0, 10); }
  };

  const canonical = article ? `${SITE_URL}/${seg}/blog/${article.slug}` : '';
  const isUpdated = article && article.updated_at && article.published_at && article.updated_at !== article.published_at
    && new Date(article.updated_at).getTime() - new Date(article.published_at).getTime() > 60 * 1000;

  // hreflang alternates: only translations with noindex=false
  const alternates = article ? (article.translations || []).filter(x => !x.noindex) : [];
  const xDefault = article ? article.x_default_url : '';

  const jsonLd = article ? {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.meta_description,
    "image": article.cover_url ? [article.cover_url] : undefined,
    "author": { "@type": "Organization", "name": article.author || "STUDYGRI Team" },
    "publisher": {
      "@type": "Organization",
      "name": "STUDYGRI",
      "logo": { "@type": "ImageObject", "url": `${SITE_URL}/logo512.png` }
    },
    "datePublished": article.published_at,
    "dateModified": article.updated_at || article.published_at,
    "mainEntityOfPage": canonical,
    ...(article.reviewed_by ? { "reviewedBy": { "@type": "Person", "name": article.reviewed_by } } : {}),
  } : null;

  return (
    <div className="min-h-screen" style={{ background: '#FAF8F5' }}>
      {article && (
        <Helmet>
          <title>{article.meta_title || article.title}</title>
          <meta name="description" content={article.meta_description} />
          <link rel="canonical" href={canonical} />
          {article.noindex && <meta name="robots" content="noindex, follow" />}

          {alternates.map(a => (
            <link key={a.hreflang} rel="alternate" hrefLang={a.hreflang} href={a.url} />
          ))}
          {xDefault && !article.noindex && <link rel="alternate" hrefLang="x-default" href={xDefault} />}

          <meta property="og:type" content="article" />
          <meta property="og:title" content={article.meta_title || article.title} />
          <meta property="og:description" content={article.meta_description} />
          <meta property="og:url" content={canonical} />
          {article.cover_url && <meta property="og:image" content={article.cover_url} />}
          <meta property="og:locale" content={LOCALE_TO_HTML_LANG[lang] || lang} />

          {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
        </Helmet>
      )}

      <Header onOpenForm={() => setFormOpen(true)} />

      <main className="pt-28 md:pt-36 pb-20">
        <article className="max-w-3xl mx-auto px-5 md:px-8">
          <Link to="/blog" className="inline-block text-sm font-semibold mb-6 hover:text-[#C9A96E]" style={{ color: '#1B2A4A' }} data-testid="blog-back">
            {t.back}
          </Link>

          {loading || !article ? (
            <div className="animate-pulse space-y-4">
              <div className="h-10 bg-white rounded" />
              <div className="h-4 bg-white rounded w-1/3" />
              <div className="aspect-[16/9] bg-white rounded-2xl" />
            </div>
          ) : (
            <>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-tight mb-4" style={{ color: '#1B2A4A' }} data-testid="article-title">
                {article.title}
              </h1>

              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm mb-2" style={{ color: '#666' }}>
                <span className="font-semibold" style={{ color: '#1B2A4A' }} data-testid="article-author">{article.author || 'STUDYGRI Team'}</span>
                <span>·</span>
                <span data-testid="article-date">{fmtDate(article.published_at)}</span>
                {isUpdated && (<><span>·</span><span data-testid="article-updated">{t.updated}: {fmtDate(article.updated_at)}</span></>)}
              </div>
              {article.reviewed_by && (
                <div className="text-sm mb-8" style={{ color: '#5a5a5a' }} data-testid="article-reviewed">
                  {t.reviewed}: <span className="font-semibold" style={{ color: '#1B2A4A' }}>{article.reviewed_by}</span>
                </div>
              )}

              {article.cover_url && (
                <div className="rounded-2xl overflow-hidden mb-10" style={{ border: '1px solid #E8E0D5' }}>
                  <img src={article.cover_url} alt={article.title} className="w-full h-auto" />
                </div>
              )}

              <div className="blog-body prose prose-lg max-w-none" data-testid="article-body">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{article.body_md}</ReactMarkdown>
              </div>

              {article.source_urls && article.source_urls.length > 0 && (
                <aside className="mt-14 pt-8 border-t" style={{ borderColor: '#E8E0D5' }} data-testid="article-sources">
                  <h3 className="font-display text-xl mb-4" style={{ color: '#1B2A4A' }}>{t.sources}</h3>
                  <ul className="space-y-2 text-sm">
                    {article.source_urls.map((u, i) => (
                      <li key={i} className="break-words">
                        <a href={u} target="_blank" rel="noopener noreferrer" className="hover:text-[#C9A96E] underline underline-offset-2" style={{ color: '#1B2A4A' }}>{u}</a>
                      </li>
                    ))}
                  </ul>
                </aside>
              )}
            </>
          )}
        </article>
      </main>

      <Footer />
      <FloatingContact onOpenForm={() => setFormOpen(true)} />
      <ApplicationDialog open={formOpen} onOpenChange={setFormOpen} />
    </div>
  );
};

export default BlogArticlePage;
